import {
    ReadonlyMat3,
    ReadonlyVec3,
    ReadonlyVec4,
    mat4,
    vec2,
    vec3,
} from 'gl-matrix';
import { gl } from '../../../Engine';
import { SignedDistanceField } from '../../../core/models/SignedDistanceField';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import { UniformMatrix } from '../../../core/utils/uniforms/UniformMatrix';
import { UniformSampler } from '../../../core/utils/uniforms/UniformSampler';
import { UniformVec2 } from '../../../core/utils/uniforms/UniformVec2';
import { UniformVec3 } from '../../../core/utils/uniforms/UniformVec3';
import { ImageObject } from '../../image/ImageObject';
import { ContourTexture } from '../../structure/ContourTexture';
import { Grid } from '../Grid';
import { CustomTargetObject } from './CustomTargetObject';
import { StrokeMode } from '~/state/atoms/toolsAtoms';

/**
 * {@link CustomTargetObject} that adapts to the data of a {@link ImageObject} by generating an adapted sphere around a given center point.\
 * The sphere is generated using a flood fill algorithm, which is implemented in the {@link FloodFillShader}.\
 * The {@link FloodFillShader} is used to generate a {@link SignedDistanceField} that represents the sphere adapted to target {@link ImageObject}'s contents.
 *
 * In 2D mode the sphere is one voxel thick along the brush plane's normal
 * and the fill propagates in the plane only; in 3D mode it spans the
 * whole sphere and the fill propagates through the volume as well.
 */
export class AdaptiveSphere extends CustomTargetObject {
    /**
     * The amount of padding added to each side of the contour data in millimeters.\
     * This is useful to support better visualization as it provides smoother contours.
     */
    protected margin: number;

    /**
     * Diameter of the sphere in millimeters.
     */
    protected diameter: number;

    /**
     * {@link FrameBufferObject} used to {@link updateTexture update the texture} of this {@link AdaptiveSphere}.
     */
    protected fbo?: FrameBufferObject;

    /**
     * {@link FloodFillShader} used to render the adaptive sphere sdf data on the SDF texture.
     */
    protected readonly shader: FloodFillShader;

    /**
     * {@link ImageObject} the data of which the {@link AdaptiveSphere} should adapt to.
     */
    protected readonly image: ImageObject;

    /**
     * Value between 0 and 1 that determines how similar the neighboring pixels have to be to be considered part of the target volume.\
     * This is used by the {@link FloodFillShader} to determine if the neighboring pixels are similar enough to be considered part of the target volume.
     */
    private _similarityThreshold = 0.2;

    /**
     * Rotation from the brush plane's frame to PatientSpace: its columns are
     * the two in-plane axes and the plane normal.
     */
    private readonly planeOrientation: ReadonlyMat3;

    /** Whether the sphere is one slice thick (2D) or a full sphere (3D). */
    private readonly strokeMode: StrokeMode;

    /** Thickness of one SDF layer along the plane normal (mm). */
    private readonly layerThicknessMm: number;

    /**
     * {@link AdaptiveSphere} constructor.\
     * @param target {@link ImageObject} that this {@link AdaptiveSphere} should adapt to.
     * @param center {@link ReadonlyVec3 PatientSpace coordinates} of the center of the {@link AdaptiveSphere}.
     * @param diameter Diameter of the {@link AdaptiveSphere} in millimeters.
     * @param planeOrientation Rotation from the brush plane's frame to PatientSpace; the third column is the plane normal.
     *     The SDF texture is sampled at the finer of the image's voxel spacings along the two in-plane axes. Along the
     *     normal it is one voxel extent thick in 2D mode, and sampled at the image's voxel spacing in 3D mode.
     * @param strokeMode Whether the sphere is one slice thick (2D) or a full sphere (3D).
     * @param color Color of the {@link AdaptiveSphere}'s outline.
     * @param margin Amount of padding added to each side of the contour data in millimeters.
     */
    public constructor(
        target: ImageObject,
        center: ReadonlyVec3,
        diameter: number,
        planeOrientation: ReadonlyMat3,
        strokeMode: StrokeMode,
        color: ReadonlyVec4,
        margin: number = ContourTexture.DEFAULT_MARGIN
    ) {
        super(target);
        this.diameter = diameter;
        this.margin = margin;
        this._color = [color[0], color[1], color[2]];
        this._contourThickness = 0.5;
        this.renderOptions = { fillAlpha: 0.25 };
        this.shader = new FloodFillShader();
        this.image = target;
        this.planeOrientation = planeOrientation;
        this.strokeMode = strokeMode;
        const column = (index: number): vec3 =>
            vec3.fromValues(
                planeOrientation[3 * index],
                planeOrientation[3 * index + 1],
                planeOrientation[3 * index + 2]
            );
        const imageGrid = target.parent.grid;
        const resolution = Math.min(
            imageGrid.voxelSpacingAlong(column(0)),
            imageGrid.voxelSpacingAlong(column(1))
        );
        const layerThicknessMm =
            strokeMode === '3d'
                ? imageGrid.voxelSpacingAlong(column(2))
                : imageGrid.voxelExtentAlong(column(2));
        this.layerThicknessMm = layerThicknessMm;

        const sizeInPixels = this.computeSizeInPixels(resolution);
        const frame = this.frameCenteredOn(center, sizeInPixels, resolution);
        // The texture's sampling frame carries the brush plane's
        // orientation and spacing, and no extent: the sphere's SDF is a
        // shape in its own right, not a crop of an image, so the renderer
        // must not clip it to an image's voxel-center range.
        const planeFrame = new Grid(
            0,
            0,
            0,
            resolution,
            resolution,
            layerThicknessMm,
            frame.getTransformation()
        );
        this._contourTexture = new ContourTexture(
            planeFrame,
            sizeInPixels,
            frame.position,
            this.margin,
            vec3.fromValues(resolution, resolution, layerThicknessMm)
        );
        try {
            this.updateTexture();
        } catch (error) {
            this.delete();
            throw error;
        }
    }

    /**
     * Method to {@link SignedDistanceField.bind bind} the ContourTexture's SDF.
     * @returns True to indicate that the {@link SignedDistanceField} was successfully bound.
     */
    public bindReadTexture(): boolean {
        this._contourTexture!.signedDistanceField.bind();
        return true;
    }

    /**
     * Computes the size in voxels based on diameter, margin, and
     * resolution. Along the normal a 2D sphere is a single layer; a 3D
     * sphere has as many layers as its diameter covers. Every extent is
     * rounded up and made odd so the flood fill seed lands on the centre
     * voxel.
     * @param resolution The planar resolution in mm/px.
     */
    private computeSizeInPixels(resolution: number): vec3 {
        const planarSize = oddCeil(this.brushSizeMm / resolution);
        const layers =
            this.strokeMode === '3d'
                ? oddCeil(this.brushSizeMm / this.layerThicknessMm)
                : 1;
        return vec3.fromValues(planarSize, planarSize, layers);
    }

    /**
     * The brush-plane grid of `sizeInPixels` voxels centered on `center`.
     * @param resolution The planar resolution in mm/px.
     */
    private frameCenteredOn(
        center: ReadonlyVec3,
        sizeInPixels: ReadonlyVec3,
        resolution: number
    ): Grid {
        return Grid.centeredOn(
            center,
            this.planeOrientation,
            sizeInPixels,
            vec3.fromValues(resolution, resolution, this.layerThicknessMm)
        );
    }

    public get radius(): number {
        return this.diameter * 0.5;
    }

    public get brushSizeMm(): number {
        return this.diameter + 2 * this.margin;
    }

    public get grid(): Grid {
        return this._contourTexture!.textureGrid;
    }

    /**
     * Method to update the {@link diameter} of the {@link AdaptiveSphere}.
     * @param size The new {@link diameter} in millimeters.
     */
    public resize(size: number): void {
        const currentCenter = vec3.clone(this.grid.center);
        this.diameter = size;
        const resolution = this.grid.resX;
        const sizeInPixels = this.computeSizeInPixels(resolution);
        const corner = this.frameCenteredOn(
            currentCenter,
            sizeInPixels,
            resolution
        ).position;
        this._contourTexture!.updateGrid(sizeInPixels, corner);
        this.updateTexture();
    }

    public setPosition(position: ReadonlyVec3): void {
        const grid = this.grid;
        const sizeInPixels = grid.getSize();
        const corner = this.frameCenteredOn(
            position,
            sizeInPixels,
            grid.resX
        ).position;
        this._contourTexture!.updateGrid(sizeInPixels, corner);
        this.updateTexture();
    }

    /**
     * Method to update the SDF of this {@link AdaptiveSphere}.\
     * Uses ping-pong flood fill between two temporary work buffers.
     * Each iteration propagates the fill by one voxel on every layer.
     * After completion the result is copied into the ContourTexture's
     * SDF and both work buffers are deleted. The program, vertex array
     * and framebuffer the fill binds are unbound, the clear colour it
     * sets is put back to the caller's, and the work buffer released,
     * whether or not the fill completes.
     */
    protected updateTexture(): void {
        const grid = this.grid;
        if (grid.isEmpty) {
            throw new Error(
                `Attempt to update the texture of AdaptiveSphere with an empty Grid`
            );
        }
        const sizeInPixels = grid.getSize();
        const planarSize = vec2.fromValues(sizeInPixels[0], sizeInPixels[1]);
        const layers = sizeInPixels[2];

        const primarySdf = this._contourTexture!.signedDistanceField;
        const workBuffer = new SignedDistanceField(sizeInPixels);

        try {
            if (this.fbo) {
                if (!vec2.equals(this.fbo.size, planarSize)) {
                    this.fbo.resize(planarSize);
                }
            } else {
                this.fbo = new FrameBufferObject(planarSize);
            }
            const iterations = Math.ceil(
                Math.max(sizeInPixels[0], sizeInPixels[1], layers) * 0.5
            );
            const vao = RenderStorage.getQuadVAO();
            // Context-wide state the fill overwrites; the view layers clear
            // with whatever it holds, so it is restored on the way out.
            const callerClearColour = gl.getParameter(
                gl.COLOR_CLEAR_VALUE
            ) as Float32Array;
            try {
                this.fbo.bind();
                gl.clearColor(0, 0, 0, 1);
                gl.viewport(0, 0, planarSize[0], planarSize[1]);

                gl.activeTexture(gl.TEXTURE1);
                this.image.bindTexture();
                gl.activeTexture(gl.TEXTURE0);

                vao.bind(0);

                this.shader.start();
                this.shader.voxelStep.loadVec3(
                    vec3.divide(
                        vec3.create(),
                        vec3.fromValues(1, 1, 1),
                        sizeInPixels
                    )
                );

                const patientSpaceCenter = grid.center;
                const mouseImageCoords =
                    this.image.grid.worldToTextureUVPosition(
                        patientSpaceCenter
                    );

                this.shader.mouseImageCoords.loadVec3(mouseImageCoords);
                this.shader.similarityThreshold.loadFloat(
                    this._similarityThreshold
                );
                if (this.image.windowLevel) {
                    this.shader.windowLevel.loadVec2(
                        this.image.windowLevel.asVec2
                    );
                }
                this.shader.radius.loadFloat(this.diameter * 0.5);
                this.shader.margin.loadFloat(this.margin);
                // Half of the texture's extent along each of its axes, in mm:
                // what a texture coordinate of 1 measures from the centre.
                this.shader.halfExtentMm.loadVec3(
                    vec3.fromValues(
                        0.5 * sizeInPixels[0] * grid.resX,
                        0.5 * sizeInPixels[1] * grid.resY,
                        0.5 * sizeInPixels[2] * grid.resZ
                    )
                );

                // Maps sphere grid clip space [-1,1]^3 to image texture UV [0,1]^3
                // via world space: clipToImageUV = worldToTextureUV * clipToWorld
                const clipToWorld = mat4.invert(
                    mat4.create(),
                    grid.worldToClipSpace
                );
                const toImageTextureUV = mat4.multiply(
                    mat4.create(),
                    this.image.grid.worldToTextureUV,
                    clipToWorld
                );
                this.shader.toImageTextureCoords.loadMatrix(toImageTextureUV);

                // Ping-pong between the ContourTexture's SDF and a
                // temporary work buffer.
                const buffers = [primarySdf, workBuffer];

                for (let i = 0; i < iterations; ++i) {
                    const read = buffers[~i & 1];
                    read.bind();
                    const write = buffers[i & 1];
                    for (let layer = 0; layer < layers; ++layer) {
                        // Clip-space depth of the layer's centre, in [-1, 1].
                        this.shader.k.loadFloat(
                            ((layer + 0.5) / layers) * 2 - 1
                        );
                        this.fbo.createTextureLayer(write.texture, layer);
                        gl.clear(gl.COLOR_BUFFER_BIT);
                        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
                    }
                }
            } finally {
                this.shader.stop();
                vao.unbind(0);
                this.fbo.unbind();
                gl.clearColor(
                    callerClearColour[0],
                    callerClearColour[1],
                    callerClearColour[2],
                    callerClearColour[3]
                );
            }

            // If the result ended up in the work buffer, copy it
            // into the ContourTexture's SDF.
            const lastWriteIndex = (iterations - 1) & 1;
            if (lastWriteIndex === 1) {
                this._contourTexture!.resetSdfData(workBuffer.pixels);
            }
        } finally {
            workBuffer.delete();
        }
    }

    /**
     * @Override
     * Method to delete this {@link CustomTargetObject}.\
     * This calls the {@link CustomTargetObject.delete delete} method of the {@link CustomTargetObject} superclass, which deletes the ContourTexture (and its SDF).\
     * But it also deletes the work buffer, {@link fbo} and shaders.
     * @returns True to indicate that the {@link CustomTargetObject} was successfully deleted.
     */
    public delete(): boolean {
        super.delete();
        this.shader.delete();
        if (this.fbo) {
            this.fbo.delete();
        }
        return true;
    }
}

/** The smallest odd integer no less than `value`. */
function oddCeil(value: number): number {
    const size = Math.ceil(value);
    return size & 1 ? size : size + 1;
}

class FloodFillShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
    precision highp float;
    precision highp sampler3D;

    in vec2 position;

    out vec3 brushTextureCoords;
    out vec3 imageCoords;

    out float imageValue;

    uniform sampler3D imageTexture;

    uniform float k; // value in between -1 and 1
    uniform mat4 toImageTextureCoords;

    uniform vec2 windowLevel;

    uniform vec3 mouseImageCoords;

    float valueModification(float value){
        value = (value - windowLevel.y + (windowLevel.x * 0.5)) / windowLevel.x;
        return clamp(value, 0.0, 1.0);
    }

    void main() {
        gl_Position = vec4(position, 0.0, 1.0);
        brushTextureCoords = vec3(position, k);
        imageCoords = (toImageTextureCoords * vec4(position.x, position.y, k, 1.0)).xyz;
        imageValue = valueModification(texture(imageTexture, mouseImageCoords).r);
    }`;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
    precision highp float;
    precision highp sampler3D;

    in vec3 brushTextureCoords;
    in vec3 imageCoords;

    in float imageValue;

    out float value;

    uniform sampler3D brushTexture;
    uniform sampler3D imageTexture;
    uniform vec3 voxelStep; // 1 / sizeInPixels, one voxel step in texture UV [0,1]
    uniform vec3 halfExtentMm; // mm from the centre to each face of the texture
    uniform float similarityThreshold;
    uniform float radius;
    uniform float margin;
    uniform vec2 windowLevel;

    float valueModification(float value){
        value = (value - windowLevel.y + (windowLevel.x * 0.5)) / windowLevel.x;
        return clamp(value, 0.0, 1.0);
    }

    void main() {
        if(imageCoords.x < 0.0 || imageCoords.x > 1.0 || imageCoords.y < 0.0 || imageCoords.y > 1.0 || imageCoords.z < 0.0 || imageCoords.z > 1.0) {
            discard;
        }

        // in [0, 1] range, with (0.5, 0.5, 0.5) in the center of the texture
        vec3 textureCoords = (brushTextureCoords + vec3(1.0)) * 0.5;
        vec3 fromCenter = abs(textureCoords - vec3(0.5));

        // Seed the centre voxel to start the flood fill. The centre voxel's
        // own coordinate is 0 from the centre and its neighbours' are one
        // voxel step, so half a step tells them apart without depending on
        // how the step rounds.
        if(all(lessThan(fromCenter, 0.5 * voxelStep))) {
            value = 1.0;
            return;
        }

        float distFromCenterMm = length(brushTextureCoords * halfExtentMm);
        // Cap limits SDF to the sphere shape: 0.5 at surface, falls to 0 at margin edge
        float cap = 0.5 - 0.5 * (distFromCenterMm - radius) / margin;

        value = texture(brushTexture, textureCoords).r;
        bool filled = value >= 0.5;
        if(filled) { value = 1.0; }

        bool hasFilledNeighbor = false;
        int filledCount = 0;
        int neighborCount = 0;

        // Flood fill over the 26-neighbourhood. A single-layer texture has
        // no neighbours along z, so the fill then propagates in-plane only.
        for(float x = -1.0; x <= 1.0; ++x){
            for(float y = -1.0; y <= 1.0; ++y){
                for(float z = -1.0; z <= 1.0; ++z){
                    if(x == 0.0 && y == 0.0 && z == 0.0) { continue; }

                    vec3 delta = vec3(x, y, z) * voxelStep;
                    vec3 neighborCoords = textureCoords + delta;
                    if(any(lessThan(neighborCoords, vec3(0.0))) || any(greaterThan(neighborCoords, vec3(1.0)))) {
                        continue;
                    }

                    neighborCount++;
                    float ref = texture(brushTexture, neighborCoords).r;
                    float stepDist = length(delta * halfExtentMm) / margin;

                    if(!filled) {
                        if(ref > 0.5) {
                            hasFilledNeighbor = true;
                            filledCount++;
                        } else {
                            value = max(ref - stepDist, value);
                        }
                    } else {
                        // Smoothing: maintain SDF distance near boundaries
                        value = min(ref < 0.5 ? 0.5 + stepDist : ref + stepDist, value);
                    }
                }
            }
        }

        // Only fill if connected to existing structure AND image is similar
        if(!filled && hasFilledNeighbor) {
            float refImageValue = valueModification(texture(imageTexture, imageCoords).r);
            float deltaImage = abs(imageValue - refImageValue) / similarityThreshold;
            float similarity = 1.0 - deltaImage;
            // Neighbor density: more filled neighbors makes filling easier
            // Fills small holes, reduces narrow bridges
            float neighborSupport = float(filledCount) / max(float(neighborCount), 1.0);
            float fillStrength = mix(similarity, 1.0, neighborSupport * neighborSupport);
            value = max(0.5 + 0.5 * fillStrength, value);
        }

        value = min(value, cap);
    }
    `;

    public readonly toImageTextureCoords: UniformMatrix;

    public readonly k: UniformFloat;

    private readonly brushTexture: UniformSampler;

    private readonly imageTexture: UniformSampler;

    /** One voxel step in texture UV [0,1] space (1 / sizeInPixels). */
    public readonly voxelStep: UniformVec3;

    /** Distance in mm from the texture centre to each of its faces. */
    public readonly halfExtentMm: UniformVec3;

    public readonly mouseImageCoords: UniformVec3;

    public readonly radius: UniformFloat;
    public readonly margin: UniformFloat;

    /**
     * Threshold value that determines if the neighboring pixels are similar enough to be considered part of the target volume.\
     * This should be a value in between 0 and 1.
     */
    public readonly similarityThreshold: UniformFloat;

    public readonly windowLevel: UniformVec2;

    public constructor() {
        super(
            FloodFillShader.VERTEX_CONTENT,
            FloodFillShader.FRAGMENT_CONTENT,
            'position'
        );
        this.toImageTextureCoords = new UniformMatrix('toImageTextureCoords');
        this.k = new UniformFloat('k');
        this.brushTexture = new UniformSampler('brushTexture');
        this.imageTexture = new UniformSampler('imageTexture');
        this.radius = new UniformFloat('radius');
        this.margin = new UniformFloat('margin');
        this.voxelStep = new UniformVec3('voxelStep');
        this.halfExtentMm = new UniformVec3('halfExtentMm');
        this.mouseImageCoords = new UniformVec3('mouseImageCoords');
        this.similarityThreshold = new UniformFloat('similarityThreshold');
        this.windowLevel = new UniformVec2('windowLevel');
        super.storeUniformLocations(
            this.toImageTextureCoords,
            this.k,
            this.brushTexture,
            this.imageTexture,
            this.voxelStep,
            this.halfExtentMm,
            this.mouseImageCoords,
            this.radius,
            this.margin,
            this.similarityThreshold,
            this.windowLevel
        );
        super.start();
        this.brushTexture.loadTextureUnit(0);
        this.imageTexture.loadTextureUnit(1);
        super.stop();
    }
}
